// @ts-nocheck

// App
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import FocusLock from 'react-focus-lock';
import { useYieldCallback } from '@zillow/yield-callback';

// Lodash
import isEqual from 'lodash/isEqual';

// Icons
import CalendarIcon from 'images/icons/calendar.min.svg';
import CalendarIconTeal from 'images/icons/calendar-teal.min.svg';

// Components
import Calendar from 'app/shared/modules/MobileSRP/Header/FilterModules/MoveInDateFilter/Calendar';
import Checkbox from 'app/shared/modules/MobileSRP/__BaseComponents__/Checkbox';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Tracking

// Misc / utils
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import dateUtils from 'app/shared/utils/dateUtils';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';

const userTimezone = 'T00:00:00.000';
const MoveInDateFilter = () => {
  // React refs
  const calendarRef = useRef(null);
  const buttonRef = useRef(null);

  // Redux
  const availabilityState = useSelector((state) => state.filter.availability);

  // React state
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterState, setFilterState] = useState({
    start: availabilityState.start ? new Date(availabilityState.start + userTimezone) : null,
    end: availabilityState.end ? new Date(availabilityState.end + userTimezone) : null,
    hideUnknownAvailabilityDate: availabilityState.hideUnknownAvailabilityDate || false,
  });
  const [value, setValue] = useState(
    availabilityState.end ? dateUtils.formatDateMmddyyyy(availabilityState.end + userTimezone) : 'Any date',
  );

  const today = new Date();

  // Hooks
  const { reduxOnlyChange, resetFilter } = useFilterChange();

  // Sync filter state to Redux when dates are selected
  const syncToRedux = useCallback(
    (state: typeof filterState) => {
      const hasStartAndEndDates = state.start && state.end;
      if (hasStartAndEndDates) {
        const formattedStartDate = dateUtils.formatDateYyyymmdd(state.start).split('/').join('-');
        const formattedEndDate = dateUtils.formatDateYyyymmdd(state.end).split('/').join('-');
        reduxOnlyChange({
          availability: { ...state, start: formattedStartDate, end: formattedEndDate },
        });
      }
    },
    [reduxOnlyChange],
  );

  useEffect(() => {
    if (showDropdown) {
      calendarRef.current.focus();
    }
  }, [showDropdown]);

  const handleButtonClick = useYieldCallback(() => {
    setShowDropdown(!showDropdown);
  });

  const handleKeyDown = (e) => {
    e.stopPropagation();
    const keyCode = e.keyCode;

    if (keyCode === VALUE_CONSTANTS.ESC_KEYCODE) {
      setShowDropdown(false);
      buttonRef.current.focus();
    }
  };

  const performReset = useYieldCallback(() => {
    resetFilter({ availability: DEFAULT.availability });
    setFilterState({
      ...DEFAULT.availability,
    });
    setValue('Any date');
  });

  const handleResetButtonClick = () => {
    performReset();
  };

  const handleOnDayClick = useYieldCallback((day, modifiers) => {
    if (modifiers.disabled) {
      return;
    }

    const now = new Date(day);
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 29));
    const dayValue = dateUtils.formatDateMmddyyyy(day);
    const endValue = dateUtils.formatDateMmddyyyy(filterState.end);

    if (isEqual(dayValue, endValue)) {
      performReset();
    } else {
      if (day >= today) {
        const newFilterState = {
          ...filterState,
          start: thirtyDaysAgo,
          end: day,
        };
        setFilterState(newFilterState);
        setValue(dayValue);
        syncToRedux(newFilterState);
      }
    }
  });

  const handleCheckboxClick = () => {
    const newFilterState = {
      ...filterState,
      hideUnknownAvailabilityDate: !filterState.hideUnknownAvailabilityDate,
    };
    setFilterState(newFilterState);
    syncToRedux(newFilterState);
  };

  const shouldDisplayListingNote = Boolean(value !== 'Any date');
  const shouldDisplayOnOrBeforDateeNote = Boolean(!showDropdown && value !== 'Any date');
  const shouldDisplayCheckbox = Boolean(showDropdown && filterState.start !== null && filterState.end !== null);

  return (
    <GeneralFilterSection>
      <GeneralFilterHeader>
        <GeneralFilterLabel id="move-date">Move-in date</GeneralFilterLabel>
      </GeneralFilterHeader>
      {shouldDisplayOnOrBeforDateeNote && (
        <S.OnOrBeforeDateNote id="move-inst">On or before the following date:</S.OnOrBeforeDateNote>
      )}
      <FocusLock disabled={!showDropdown}>
        <S.MoveInDateButton
          ariaExpanded={showDropdown}
          ariaLabeledBy={'move-date'}
          ariaDescribedBy={'move-inst selected-date'}
          icon={showDropdown ? CalendarIcon : CalendarIconTeal}
          iconWidth={'16px'}
          iconHeight={'16px'}
          ariaLabel={'Select your desired move in date'}
          onClick={handleButtonClick}
          ref={buttonRef}
        >
          <span id="selected-date">{value}</span>
        </S.MoveInDateButton>
        {showDropdown && (
          <article>
            <S.MoveInDateFilterSection $shouldCollapseSpacing={shouldDisplayListingNote}>
              <GeneralFilterLabel>Select your move-in date</GeneralFilterLabel>
              <GeneralFilterResetButton onClick={handleResetButtonClick} aria-label={'Reset move in date filter'}>
                Reset
              </GeneralFilterResetButton>
              {shouldDisplayListingNote && (
                <S.ListingsNote>
                  Listings with a move-in date in this window will be included in your search:
                </S.ListingsNote>
              )}
            </S.MoveInDateFilterSection>
            <Calendar
              handleOnDayClick={handleOnDayClick}
              today={today}
              from={filterState.start}
              to={filterState.end}
              customRef={calendarRef}
              handleEsc={handleKeyDown}
            />
            {shouldDisplayCheckbox && (
              <Checkbox
                data-option-value={'hideUnknownAvailabilityDate'}
                onChange={handleCheckboxClick}
                isChecked={filterState.hideUnknownAvailabilityDate}
                ariaLabel={'Hide listings with unknown availability dates'}
                flex
              >
                Hide places with no date provided
              </Checkbox>
            )}
          </article>
        )}
      </FocusLock>
    </GeneralFilterSection>
  );
};

export default MoveInDateFilter;
